  <script>
  import MetisMenu from "metismenujs/dist/metismenujs";
  import { mapState } from "vuex";
  import { isMenuItemVisible, isMenuChildVisible, isMenuGroupVisible, GRUPOS_RANKINGS } from "@/utils/menu-map";
  import { RANKINGS_NACIONAIS } from "@/utils/ranking-nacional";
  import { RANKINGS_ESTADUAIS, carregarContextoEstadual, contextoEstadual } from "@/utils/ranking-estadual";
  import { isEmbeddedWebView } from "@/utils/webview";
  import { api } from "@/state/services";

  export default {
    locales: {
      pt: {},
      es: {},
    },
    data() {
      return {
        metis: null,
        communityModalVisible: false,
        communityIframeSrc: null
      };
    },
    computed: {
      ...mapState({
        account: (state) => state.account,
      }),
      // A condição em si mora em utils/menu-map.js (fonte única de verdade,
      // compartilhada com o tour de boas-vindas). Este computed continua
      // existindo porque o watch abaixo depende dele para reinicializar o
      // MetisMenu quando o item aparece.
      // Sub-grupos de "Rankings" (Nacional Brasil/Truck, Estadual Brasil/Truck > rankings), já
      // filtrados pelo que o usuário enxerga (regras em utils/menu-map.js). Os do Estadual só
      // aparecem para quem pertence a uma diretoria.
      gruposRankings() {
        const todos = [...RANKINGS_NACIONAIS.map((r) => ({ ...r, tipo: "nacional" })), ...RANKINGS_ESTADUAIS.map((r) => ({ ...r, tipo: "estadual" }))];
        return GRUPOS_RANKINGS.filter((g) => this.groupVisible("rankings", g.key)).map((g) => ({
          ...g,
          estadual: g.key.indexOf("estadual-") === 0,
          itens: todos.filter((r) => `${r.tipo}-${r.grupo}` === g.key && this.childVisible("rankings", r.id)),
        }));
      },
      // Rankings > Ranking Nacional / Ranking Estadual > Brasil / Truck > rankings.
      tiposRankings() {
        return [
          { key: "nacional", label: "Ranking Nacional" },
          { key: "estadual", label: "Ranking Estadual" },
        ]
          .map((t) => ({
            ...t,
            grupos: this.gruposRankings
              .filter((g) => g.key.indexOf(t.key + "-") === 0)
              .map((g) => ({ ...g, label: g.key.slice(t.key.length + 1) === "truck" ? "Truck" : "Brasil" })),
          }))
          .filter((t) => t.grupos.length);
      },
      estadualMenu() {
        return this.groupVisible("rankings", "estadual-brasil") || this.groupVisible("rankings", "estadual-truck");
      },
      // Nome da diretoria no `title` do item (dica no desktop); na tela ele aparece em destaque no título.
      diretoriaEstadual() {
        return contextoEstadual.diretoria ? contextoEstadual.diretoria.label : "";
      },
      circleMenu() {
        return this.menuVisible("comunidade");
      },
    },
    watch: {
      // Monitora o circleMenu e reinicializa o MetisMenu quando ele aparecer
      circleMenu: {
        handler(val) {
          if (val) {
            this.$nextTick(() => {
              // Destrói a instância anterior se existir para evitar bugs de memória
              if (this.metis) this.metis.dispose();
              this.metis = new MetisMenu("#side-menu");
            });
          }
        },
        immediate: true
      },
      // Os sub-grupos do Estadual (dentro de "Rankings") dependem de uma chamada à API (diretoria):
      // quando aparecem depois da montagem, o MetisMenu precisa ser refeito para o acordeão funcionar.
      estadualMenu(val) {
        if (!val) return;
        this.$nextTick(() => {
          if (this.metis) this.metis.dispose();
          this.metis = new MetisMenu("#side-menu");
          // Primeira visita direto numa tela do Ranking Estadual: o item nasceu depois do mounted().
          if (window.location.pathname.indexOf("/biteams/rk-estadual-") === 0) this.marcarItemAtivo();
        });
      },
    },
    created() {
      // No-op quando a diretoria desta sessão já foi carregada (fica no localStorage).
      carregarContextoEstadual();
    },
    mounted() {
      document.body.setAttribute("data-sidebar", "light");
      
      // Inicialização padrão do MetisMenu
      this.metis = new MetisMenu("#side-menu");

      this.marcarItemAtivo();
    },
    methods: {
      // Marca o link da rota atual e abre os acordeões acima dele (lógica original do mounted()).
      marcarItemAtivo() {
        // Lógica para marcar o link ativo no menu (Preservada do seu código original)
        var links = document.getElementsByClassName("side-nav-link-ref");
        var matchingMenuItem = null;
        for (var i = 0; i < links.length; i++) {
          if (window.location.pathname === links[i].pathname) {
            matchingMenuItem = links[i];
            break;
          }
        }

        if (matchingMenuItem) {
          matchingMenuItem.classList.add("active");
          var parent = matchingMenuItem.parentElement;
          if (parent) {
            parent.classList.add("mm-active");
            // Sobe por todos os níveis de sub-menu (Rankings chega a 4) abrindo cada acordeão.
            var ul = parent.parentElement && parent.parentElement.closest("ul");
            while (ul && ul.id !== "side-menu") {
              ul.classList.add("mm-show");
              const li = ul.parentElement;
              if (!li) break;
              li.classList.add("mm-active");
              for (var c = 0; c < li.children.length; c++) {
                const filho = li.children[c];
                if (filho.classList.contains("has-arrow") || filho.classList.contains("has-dropdown")) {
                  filho.classList.add("mm-active");
                }
              }
              ul = li.parentElement && li.parentElement.closest("ul");
            }
          }
        }
      },
      // Visibilidade de item e subitem: as regras (módulo contratado,
      // permissão da secretária, flags de perfil) ficam em utils/menu-map.js,
      // que também alimenta o tour de boas-vindas. Enquanto as duas telas
      // consumirem este mesmo módulo, o tour não tem como apontar para um item
      // que o usuário não vê.
      menuVisible(key) {
        return isMenuItemVisible(this.account.user, key);
      },
      childVisible(parentKey, childKey) {
        return isMenuChildVisible(this.account.user, parentKey, childKey);
      },
      groupVisible(parentKey, groupKey) {
        return isMenuGroupVisible(this.account.user, parentKey, groupKey);
      },
      async goToBiteams() {
        try {
          const response = await api.post("biteams-sso/handoff");
          if (response.data && response.data.status === "success") {
            window.location.href = `${process.env.VUE_APP_URL_BITEAMS}sso?ticket=${response.data.ticket}`;
          }
        } catch (error) {
          // Item só aparece com o módulo ativo; falha aqui é caso de rede/API fora do ar.
        }
      },
      async goToTeamscrm() {
        try {
          const response = await api.post("teamscrm-sso/handoff");
          if (response.data && response.data.status === "success") {
            window.location.href = `${process.env.VUE_APP_URL_CRM}sso?ticket=${response.data.ticket}`;
          }
        } catch (error) {
          // Item só aparece com o módulo ativo; falha aqui é caso de rede/API fora do ar.
        }
      },
      getCircleLink() {
        const token = localStorage.getItem('token');

        return `https://api.salesteams.com.br/oauth/authorize` +
              `?client_id=circle` +
              `&redirect_uri=https://comunidade.salesteams.com.br/oauth2/callback` +
              `&response_type=code` +
              `&token=${token}`;
      },
      onCommunityClick(event) {
        // No app (webview), a Comunidade abre uma nova aba que não tem "voltar" para o sistema.
        // Nesse caso mantemos o usuário dentro do app, exibindo a Comunidade num modal com iframe.
        if (!isEmbeddedWebView()) {
          return;
        }

        event.preventDefault();
        this.communityIframeSrc = this.getCircleLink();
        this.communityModalVisible = true;
      },
      closeCommunityModal() {
        this.communityModalVisible = false;
        this.communityIframeSrc = null;
      }
    },
  };
  </script>
  <template>
    <!-- landmark <nav>: sem ele o menu principal não aparece na lista de
         regiões do leitor de tela e o usuário não consegue pular direto para
         a navegação. -->
    <nav id="sidebar-menu" aria-label="Menu principal">
      <!--
        Os atributos data-tour="menu-*" são os âncoras do tour de boas-vindas
        (components/onboarding-tour.vue). São SÓ atributos: nenhum nó novo
        entra na árvore <ul>/<li>, porque o realce do item ativo é feito na
        mão percorrendo o DOM no mounted() acima e qualquer elemento extra
        aqui dentro o quebraria.
      -->
      <ul id="side-menu" class="metismenu list-unstyled mt-1">
        <li v-if="menuVisible('home')" data-tour="menu-home">
          <router-link tag="a" to="/" class="side-nav-link-ref">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M5 22h14a2 2 0 0 0 2-2v-9a1 1 0 0 0-.29-.71l-8-8a1 1 0 0 0-1.41 0l-8 8A1 1 0 0 0 3 11v9a2 2 0 0 0 2 2zm5-2v-5h4v5zm-5-8.59 7-7 7 7V20h-3v-5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v5H5z">
              </path>
            </svg>
            <span class="notranslate">Dashboard</span>
          </router-link>
        </li>
        <li v-if="menuVisible('minhas-metas')" data-tour="menu-minhas-metas">
          <router-link tag="a" to="/minhas-metas" class="side-nav-link-ref">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8z"></path>
              <path d="M12 6c-3.309 0-6 2.691-6 6s2.691 6 6 6 6-2.691 6-6-2.691-6-6-6zm0 10c-2.206 0-4-1.794-4-4s1.794-4 4-4 4 1.794 4 4-1.794 4-4 4z"></path>
              <path d="M12 10c-1.103 0-2 .897-2 2s.897 2 2 2 2-.897 2-2-.897-2-2-2z"></path>
            </svg>
            <span class="notranslate">Minhas Metas</span>
          </router-link>
        </li>
        <li v-if="menuVisible('meu-cartao')" data-tour="menu-meu-cartao">
          <router-link tag="a" to="/meu-cartao" class="side-nav-link-ref">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M20 4H4c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h16c1.103 0 2-.897 2-2V6c0-1.103-.897-2-2-2zm0 14H4V6h16l.002 12z"></path>
              <circle cx="8.5" cy="10.5" r="2"></circle>
              <path d="M5 16.5c0-1.5 1.567-2.5 3.5-2.5s3.5 1 3.5 2.5V17H5v-.5zM14 9h5v2h-5zm0 4h4v2h-4z"></path>
            </svg>
            <span class="notranslate">Meu Cartão</span>
          </router-link>
        </li>
        <li v-if="menuVisible('informativos')" data-tour="menu-informativos">
          <router-link tag="a" to="/informatives" class="side-nav-link-ref">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path
                d="M19.875 3H4.125C2.953 3 2 3.897 2 5v14c0 1.103.953 2 2.125 2h15.75C21.047 21 22 20.103 22 19V5c0-1.103-.953-2-2.125-2zm0 16H4.125c-.057 0-.096-.016-.113-.016-.007 0-.011.002-.012.008L3.988 5.046c.007-.01.052-.046.137-.046h15.75c.079.001.122.028.125.008l.012 13.946c-.007.01-.052.046-.137.046z">
              </path>
              <path d="M6 7h6v6H6zm7 8H6v2h12v-2h-4zm1-4h4v2h-4zm0-4h4v2h-4z"></path>
            </svg>
            <span class="notranslate">Informativos</span>
          </router-link>
        </li>
        <li v-if="menuVisible('eventos')" data-tour="menu-eventos">
          <router-link tag="a" to="/events" class="side-nav-link-ref">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M7 11h2v2H7zm0 4h2v2H7zm4-4h2v2h-2zm0 4h2v2h-2zm4-4h2v2h-2zm0 4h2v2h-2z"></path>
              <path d="M5 22h14c1.103 0 2-.897 2-2V6c0-1.103-.897-2-2-2h-2V2h-2v2H9V2H7v2H5c-1.103 0-2 .897-2 2v14c0 1.103.897 2 2 2zM19 8l.001 12H5V8h14z"></path>
            </svg>
            <span class="notranslate">Eventos</span>
          </router-link>
        </li>
        <li v-show="circleMenu" data-tour="menu-comunidade">
          <a target="_blank" :href="getCircleLink()" class="side-nav-link-ref" @click="onCommunityClick">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M2 7v1l11 4 9-4V7L11 4z"></path>
              <path d="M4 11v4.267c0 1.621 4.001 3.893 9 3.734 4-.126 6.586-1.972 7-3.467.024-.089.037-.178.037-.268V11L13 14l-5-1.667v3.213l-1-.364V12l-3-1z"></path>
            </svg>
            <span class="notranslate">Comunidade</span>
          </a>
        </li>
        <li v-if="false && account.user.themembers" class="d-none">
          <a target="_blank" :href="account.user.themembers" class="side-nav-link-ref">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M2 7v1l11 4 9-4V7L11 4z"></path>
              <path d="M4 11v4.267c0 1.621 4.001 3.893 9 3.734 4-.126 6.586-1.972 7-3.467.024-.089.037-.178.037-.268V11L13 14l-5-1.667v3.213l-1-.364V12l-3-1z"></path>
            </svg>
            <span class="notranslate">Teams University</span>
          </a>
        </li>
        <li v-if="menuVisible('crm')" data-tour="menu-crm">
          <a href="javascript:void(0);" class="side-nav-link-ref" @click="goToTeamscrm">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M20 4H4c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h16c1.103 0 2-.897 2-2V6c0-1.103-.897-2-2-2zM8.715 8c1.151 0 2 .849 2 2s-.849 2-2 2-2-.849-2-2 .848-2 2-2zm3.715 8H5v-.465c0-1.373 1.676-2.785 3.715-2.785s3.715 1.412 3.715 2.785V16zM19 15h-4v-2h4v2zm0-4h-5V9h5v2z"/>
            </svg>
            <span class="notranslate">CRM</span>
          </a>
        </li>
        <li v-if="menuVisible('usuarios')" data-tour="menu-usuarios">
          <router-link tag="a" to="/usuarios" class="side-nav-link-ref">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M16 11c1.657 0 3-1.567 3-3.5S17.657 4 16 4s-3 1.567-3 3.5S14.343 11 16 11zM8 11c1.657 0 3-1.567 3-3.5S9.657 4 8 4 5 5.567 5 7.5 6.343 11 8 11zm0 2c-2.67 0-6 1.34-6 4v2h12v-2c0-2.66-3.33-4-6-4zm8 0c-.31 0-.66.02-1.03.05 1.16.84 2.03 1.97 2.03 3.45V19h5v-2c0-2.66-3.33-4-6-4z"></path>
            </svg>
            <span class="notranslate">Usuários</span>
          </router-link>
        </li>
        <li v-if="menuVisible('apn')" data-tour="menu-apn">
          <router-link tag="a" to="/apn" class="side-nav-link-ref">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M20 2H4c-1.103 0-2 .897-2 2v18l4-4h14c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zm-3 9H7V9h10v2zm-4 4H7v-2h6v2zm4-8H7V5h10v2z"></path>
            </svg>
            <span class="notranslate">Convite APN</span>
          </router-link>
        </li>
        <li v-if="menuVisible('biteams')" data-tour="menu-biteams">
          <a href="javascript:void(0);" class="side-nav-link-ref" @click="goToBiteams">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M3 3h8v8H3V3zm10 0h8v5h-8V3zM3 13h8v8H3v-8zm10 3h8v5h-8v-5z"></path>
            </svg>
            <span class="notranslate">BI Teams</span>
          </a>
        </li>
        <li v-if="menuVisible('rede')" data-tour="menu-rede">
          <a class="is-parent has-arrow" href="javascript:void(0);">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M12 10c1.151 0 2-.848 2-2s-.849-2-2-2c-1.15 0-2 .848-2 2s.85 2 2 2zm0 1c-2.209 0-4 1.612-4 3.6v.386h8V14.6c0-1.988-1.791-3.6-4-3.6z"></path>
              <path d="M19 2H5c-1.103 0-2 .897-2 2v13c0 1.103.897 2 2 2h4l3 3 3-3h4c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zm-5 15-2 2-2-2H5V4h14l.002 13H14z"></path>
            </svg>
            <span class="notranslate">Rede</span>
          </a>
          <ul class="sub-menu" data-titulo="Rede">
            <li v-if="childVisible('rede', 'indicados')">
              <router-link tag="a" to="/network/indicados" class="side-nav-link-ref">
                <span class="notranslate">Indicados</span>
              </router-link>
            </li>
            <li v-if="childVisible('rede', 'diretos')">
              <router-link tag="a" to="/network/diretos" class="side-nav-link-ref">
                <span class="notranslate">Diretos</span>
              </router-link>
            </li>
            <li v-if="childVisible('rede', 'equipe')">
              <router-link tag="a" to="/network/equipe" class="side-nav-link-ref">
                <span class="notranslate">Equipe</span>
              </router-link>
            </li>
            <li v-if="childVisible('rede', 'matriz')">
              <router-link tag="a" to="/network/matriz" class="side-nav-link-ref">
                <span class="notranslate">Matriz</span>
              </router-link>
            </li>
          </ul>
        </li>
        <li class="d-none">
          <router-link tag="a" to="/wallet/extract" class="side-nav-link-ref">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path
                d="M12 15c-1.84 0-2-.86-2-1H8c0 .92.66 2.55 3 2.92V18h2v-1.08c2-.34 3-1.63 3-2.92 0-1.12-.52-3-4-3-2 0-2-.63-2-1s.7-1 2-1 1.39.64 1.4 1h2A3 3 0 0 0 13 7.12V6h-2v1.09C9 7.42 8 8.71 8 10c0 1.12.52 3 4 3 2 0 2 .68 2 1s-.62 1-2 1z">
              </path>
              <path d="M5 2H2v2h2v17a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V4h2V2H5zm13 18H6V4h12z"></path>
            </svg>
            <span class="notranslate">Financeiro</span>
          </router-link>
        </li>
        <li v-if="menuVisible('apvs-brasil')" data-tour="menu-apvs-brasil">
          <a class="is-parent has-arrow" href="javascript:void(0);">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M5 21h14c1.103 0 2-.897 2-2V5c0-1.103-.897-2-2-2H5c-1.103 0-2 .897-2 2v14c0 1.103.897 2 2 2zM5 5h14l.001 14H5V5z"></path>
              <path d="m13.553 11.658-4-2-2.448 4.895 1.79.894 1.552-3.105 4 2 2.448-4.895-1.79-.894z"></path>
            </svg>
            <span class="notranslate">APVS Brasil</span>
          </a>
          <ul class="sub-menu" data-titulo="APVS Brasil">
            <li class="d-none">
              <router-link tag="a" to="/apvs/brasil/dashboard" class="side-nav-link-ref">
                <span class="notranslate">Dashboard</span>
              </router-link>
            </li>
            <li class="d-none">
              <router-link tag="a" to="/apvs/brasil/rankings" class="side-nav-link-ref">
                <span class="notranslate">Rankings</span>
              </router-link>
            </li>
            <li v-if="childVisible('apvs-brasil', 'adesoes')">
              <router-link tag="a" to="/apvs/brasil/adesoes" class="side-nav-link-ref">
                <span class="notranslate">Adesões</span>
              </router-link>
            </li>
            <li v-if="childVisible('apvs-brasil', 'metas')">
              <router-link tag="a" to="/apvs/brasil/metas" class="side-nav-link-ref">
                <span class="notranslate">Metas</span>
              </router-link>
            </li>
            <li v-if="childVisible('apvs-brasil', 'recorrente')">
              <router-link tag="a" to="/apvs/brasil/recorrente" class="side-nav-link-ref">
                <span class="notranslate">Recorrente</span>
              </router-link>
            </li>
            <li v-if="childVisible('apvs-brasil', 'comissionamento')">
              <router-link tag="a" to="/apvs/brasil/comissionamento" class="side-nav-link-ref">
                <span class="notranslate">Vitalício</span>
              </router-link>
            </li>
            <li v-if="childVisible('apvs-brasil', 'rastreador')">
              <router-link tag="a" to="/apvs/brasil/rastreador" class="side-nav-link-ref">
                <span class="notranslate">Rastreador</span>
              </router-link>
            </li>
          </ul>
        </li>
        <li v-if="menuVisible('apvs-truck')" data-tour="menu-apvs-truck">
          <a class="is-parent has-arrow" href="javascript:void(0);">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M5 21h14c1.103 0 2-.897 2-2V5c0-1.103-.897-2-2-2H5c-1.103 0-2 .897-2 2v14c0 1.103.897 2 2 2zM5 5h14l.001 14H5V5z"></path>
              <path d="m13.553 11.658-4-2-2.448 4.895 1.79.894 1.552-3.105 4 2 2.448-4.895-1.79-.894z"></path>
            </svg>
            <span class="notranslate">APVS Truck</span>
          </a>
          <ul class="sub-menu" data-titulo="APVS Truck">
            <li class="d-none">
              <router-link tag="a" to="/apvs/truck/dashboard" class="side-nav-link-ref">
                <span class="notranslate">Dashboard</span>
              </router-link>
            </li>
            <li class="d-none">
              <router-link tag="a" to="/apvs/truck/rankings" class="side-nav-link-ref">
                <span class="notranslate">Rankings</span>
              </router-link>
            </li>
            <li v-if="childVisible('apvs-truck', 'adesoes')">
              <router-link tag="a" to="/apvs/truck/adesoes" class="side-nav-link-ref">
                <span class="notranslate">Adesões</span>
              </router-link>
            </li>
            <li v-if="childVisible('apvs-truck', 'metas')">
              <router-link tag="a" to="/apvs/truck/metas" class="side-nav-link-ref">
                <span class="notranslate">Metas</span>
              </router-link>
            </li>
            <li v-if="childVisible('apvs-truck', 'recorrente')">
              <router-link tag="a" to="/apvs/truck/recorrente" class="side-nav-link-ref">
                <span class="notranslate">Recorrente</span>
              </router-link>
            </li>
            <li v-if="childVisible('apvs-truck', 'comissionamento')">
              <router-link tag="a" to="/apvs/truck/comissionamento" class="side-nav-link-ref">
                <span class="notranslate">Vitalício</span>
              </router-link>
            </li>
            <li v-if="childVisible('apvs-truck', 'rastreador')">
              <router-link tag="a" to="/apvs/truck/rastreador" class="side-nav-link-ref">
                <span class="notranslate">Rastreador</span>
              </router-link>
            </li>
          </ul>
        </li>
        <li v-if="menuVisible('rankings')" data-tour="menu-rankings">
          <a class="is-parent has-arrow" href="javascript:void(0);">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M21 4h-3V3a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1v1H3a1 1 0 0 0-1 1v3c0 4.31 1.8 6.91 4.82 7A6 6 0 0 0 11 17.91V20H9v2h6v-2h-2v-2.09A6 6 0 0 0 17.18 15c3-.1 4.82-2.7 4.82-7V5a1 1 0 0 0-1-1zM4 8V6h2v6.83C4.22 12.08 4 9.3 4 8zm8 8a4 4 0 0 1-4-4V4h8v8a4 4 0 0 1-4 4zm6-3.17V6h2v2c0 1.3-.22 4.08-2 4.83z"></path>
            </svg>
            <span class="notranslate">Rankings</span>
          </a>
          <ul class="sub-menu" data-titulo="Rankings">
            <li v-if="childVisible('rankings', 'regulamento')">
              <router-link tag="a" to="/biteams/rk-regulamento" class="side-nav-link-ref">
                <span class="notranslate">Regras e Regulamentos</span>
              </router-link>
            </li>
            <li v-if="childVisible('rankings', 'regras')">
              <router-link tag="a" to="/biteams/rk-regras" class="side-nav-link-ref">
                <span class="notranslate">Regras</span>
              </router-link>
            </li>
            <li v-if="childVisible('rankings', 'consultores')">
              <router-link tag="a" to="/biteams/rk-consultores" class="side-nav-link-ref">
                <span class="notranslate">Consultores</span>
              </router-link>
            </li>
            <li v-if="childVisible('rankings', 'home')">
              <router-link tag="a" to="/biteams/rk-home" class="side-nav-link-ref">
                <span class="notranslate">Home Office</span>
              </router-link>
            </li>
            <li v-if="childVisible('rankings', 'executiva')">
              <router-link tag="a" to="/biteams/rk-executiva" class="side-nav-link-ref">
                <span class="notranslate">Executiva</span>
              </router-link>
            </li>
            <li v-if="childVisible('rankings', 'gerentes')">
              <router-link tag="a" to="/biteams/rk-gerentes" class="side-nav-link-ref">
                <span class="notranslate">Gerentes</span>
              </router-link>
            </li>
            <!-- 3º/4º nível: Ranking Nacional / Ranking Estadual > Brasil / Truck > rankings. O CSS
                 de #sidebar-menu prevê ul.sub-menu aninhado, e marcarItemAtivo() abre todos os
                 acordeões acima do item ativo. -->
            <li v-for="tipo in tiposRankings" :key="tipo.key">
              <a class="has-arrow" href="javascript:void(0);" :title="tipo.key === 'estadual' ? diretoriaEstadual || null : null">
                <span class="notranslate">{{ tipo.label }}</span>
              </a>
              <ul class="sub-menu">
                <li v-for="grupo in tipo.grupos" :key="grupo.key">
                  <a class="has-arrow" href="javascript:void(0);">
                    <span class="notranslate">{{ grupo.label }}</span>
                  </a>
                  <ul class="sub-menu">
                    <li v-for="item in grupo.itens" :key="item.id">
                      <router-link tag="a" :to="item.path" class="side-nav-link-ref">
                        <span class="notranslate">{{ item.menuLabel }}</span>
                      </router-link>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </li>
        <li v-if="menuVisible('afiliados')" data-tour="menu-afiliados">
          <a class="is-parent has-arrow" href="javascript:void(0);">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M12 2a5 5 0 1 0 5 5 5 5 0 0 0-5-5zm0 8a3 3 0 1 1 3-3 3 3 0 0 1-3 3zm9 11v-1a7 7 0 0 0-7-7h-4a7 7 0 0 0-7 7v1h2v-1a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v1z"></path>
            </svg>
            <span class="notranslate">Afiliados</span>
          </a>
          <ul class="sub-menu" data-titulo="Afiliados">
            <li v-if="childVisible('afiliados', 'lista')">
              <router-link tag="a" to="/afiliados/lista" class="side-nav-link-ref">
                <span class="notranslate">Meus Afiliados</span>
              </router-link>
            </li>
            <li v-if="childVisible('afiliados', 'leads')">
              <router-link tag="a" to="/afiliados/leads" class="side-nav-link-ref">
                <span class="notranslate">Leads Indicados</span>
              </router-link>
            </li>
            <li v-if="childVisible('afiliados', 'convertidos')">
              <router-link tag="a" to="/afiliados/convertidos" class="side-nav-link-ref">
                <span class="notranslate">Leads Convertidos</span>
              </router-link>
            </li>
            <li v-if="childVisible('afiliados', 'ranking')">
              <router-link tag="a" to="/afiliados/ranking" class="side-nav-link-ref">
                <span class="notranslate">Ranking</span>
              </router-link>
            </li>
          </ul>
        </li>
        <li v-if="menuVisible('acessa')" data-tour="menu-acessa">
          <a class="is-parent has-arrow" href="javascript:void(0);">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M5 21h14c1.103 0 2-.897 2-2V5c0-1.103-.897-2-2-2H5c-1.103 0-2 .897-2 2v14c0 1.103.897 2 2 2zM5 5h14l.001 14H5V5z"></path>
              <path d="m13.553 11.658-4-2-2.448 4.895 1.79.894 1.552-3.105 4 2 2.448-4.895-1.79-.894z"></path>
            </svg>
            <span class="notranslate">Acessa</span>
          </a>
          <ul class="sub-menu" data-titulo="Acessa">
            <li v-if="childVisible('acessa', 'leads')">
              <router-link tag="a" to="/acessa/leads" class="side-nav-link-ref">
                <span class="notranslate">Leads</span>
              </router-link>
            </li>
            <li v-if="childVisible('acessa', 'clientes')">
              <router-link tag="a" to="/acessa/clientes" class="side-nav-link-ref">
                <span class="notranslate">Clientes</span>
              </router-link>
            </li>
            <li v-if="childVisible('acessa', 'qrcodes')">
              <router-link tag="a" to="/acessa/qrcodes" class="side-nav-link-ref">
                <span class="notranslate">QR Codes</span>
              </router-link>
            </li>
            <li v-if="childVisible('acessa', 'adesao')">
              <router-link tag="a" to="/acessa/adesao" class="side-nav-link-ref">
                <span class="notranslate">Adesão</span>
              </router-link>
            </li>
            <li v-if="childVisible('acessa', 'recorrente')">
              <router-link tag="a" to="/acessa/recorrente" class="side-nav-link-ref">
                <span class="notranslate">Recorrente</span>
              </router-link>
            </li>
            <li v-if="childVisible('acessa', 'graduacao')">
              <router-link tag="a" to="/acessa/graduacao" class="side-nav-link-ref">
                <span class="notranslate">Graduação</span>
              </router-link>
            </li>
          </ul>
        </li>
        <li v-if="menuVisible('downloads')" data-tour="menu-downloads">
          <router-link tag="a" to="/downloads" class="side-nav-link-ref">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path
                d="M18.948 11.112C18.511 7.67 15.563 5 12.004 5c-2.756 0-5.15 1.611-6.243 4.15-2.148.642-3.757 2.67-3.757 4.85 0 2.757 2.243 5 5 5h1v-2h-1c-1.654 0-3-1.346-3-3 0-1.404 1.199-2.757 2.673-3.016l.581-.102.192-.558C8.153 8.273 9.898 7 12.004 7c2.757 0 5 2.243 5 5v1h1c1.103 0 2 .897 2 2s-.897 2-2 2h-2v2h2c2.206 0 4-1.794 4-4a4.008 4.008 0 0 0-3.056-3.888z">
              </path>
              <path d="M13.004 14v-4h-2v4h-3l4 5 4-5z"></path>
            </svg>
            <span class="notranslate">Downloads</span>
          </router-link>
        </li>
        <li v-if="menuVisible('leads')" data-tour="menu-leads">
          <router-link tag="a" to="/leads/leads" class="side-nav-link-ref">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 640 640">
              <path d="M96 64C60.7 64 32 92.7 32 128L32 200C32 213.3 42.7 224 56 224C69.3 224 80 213.3 80 200L80 128C80 119.2 87.2 112 96 112L168 112C181.3 112 192 101.3 192 88C192 74.7 181.3 64 168 64L96 64zM472 64C458.7 64 448 74.7 448 88C448 101.3 458.7 112 472 112L544 112C552.8 112 560 119.2 560 128L560 200C560 213.3 570.7 224 584 224C597.3 224 608 213.3 608 200L608 128C608 92.7 579.3 64 544 64L472 64zM80 440C80 426.7 69.3 416 56 416C42.7 416 32 426.7 32 440L32 512C32 547.3 60.7 576 96 576L168 576C181.3 576 192 565.3 192 552C192 538.7 181.3 528 168 528L96 528C87.2 528 80 520.8 80 512L80 440zM608 440C608 426.7 597.3 416 584 416C570.7 416 560 426.7 560 440L560 512C560 520.8 552.8 528 544 528L472 528C458.7 528 448 538.7 448 552C448 565.3 458.7 576 472 576L544 576C579.3 576 608 547.3 608 512L608 440zM320 280C350.9 280 376 254.9 376 224C376 193.1 350.9 168 320 168C289.1 168 264 193.1 264 224C264 254.9 289.1 280 320 280zM320 320C267 320 224 363 224 416L224 440C224 453.3 234.7 464 248 464L392 464C405.3 464 416 453.3 416 440L416 416C416 363 373 320 320 320zM512 256C512 229.5 490.5 208 464 208C437.5 208 416 229.5 416 256C416 282.5 437.5 304 464 304C490.5 304 512 282.5 512 256zM200 336.3C150.7 340.4 112 381.6 112 432L112 442.7C112 454.5 121.6 464 133.3 464L180.1 464C177.4 456.5 176 448.4 176 440L176 416C176 386.5 184.8 359.1 200 336.3zM459.9 464L506.7 464C518.5 464 528 454.4 528 442.7L528 432C528 381.7 489.3 340.4 440 336.3C455.2 359.1 464 386.5 464 416L464 440C464 448.4 462.6 456.5 459.9 464zM224 256C224 229.5 202.5 208 176 208C149.5 208 128 229.5 128 256C128 282.5 149.5 304 176 304C202.5 304 224 282.5 224 256z"></path>
            </svg>
            <span class="notranslate">Leads</span>
          </router-link>
        </li>
        <li v-if="menuVisible('financeiro')" data-tour="menu-financeiro">
          <a class="is-parent has-arrow" href="javascript:void(0);">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 640 640">
              <path d="M128 128C92.7 128 64 156.7 64 192L64 448C64 483.3 92.7 512 128 512L512 512C547.3 512 576 483.3 576 448L576 192C576 156.7 547.3 128 512 128L128 128zM360 352L488 352C501.3 352 512 362.7 512 376C512 389.3 501.3 400 488 400L360 400C346.7 400 336 389.3 336 376C336 362.7 346.7 352 360 352zM336 264C336 250.7 346.7 240 360 240L488 240C501.3 240 512 250.7 512 264C512 277.3 501.3 288 488 288L360 288C346.7 288 336 277.3 336 264zM212 208C223 208 232 217 232 228L232 232L240 232C251 232 260 241 260 252C260 263 251 272 240 272L192.5 272C185.6 272 180 277.6 180 284.5C180 290.6 184.4 295.8 190.4 296.8L232.1 303.8C257.4 308 276 329.9 276 355.6C276 381.7 257 403.3 232 407.4L232 412.1C232 423.1 223 432.1 212 432.1C201 432.1 192 423.1 192 412.1L192 408.1L168 408.1C157 408.1 148 399.1 148 388.1C148 377.1 157 368.1 168 368.1L223.5 368.1C230.4 368.1 236 362.5 236 355.6C236 349.5 231.6 344.3 225.6 343.3L183.9 336.3C158.5 332 140 310.1 140 284.5C140 255.7 163.2 232.3 192 232L192 228C192 217 201 208 212 208z"/>
            </svg>
            <span class="notranslate">Financeiro</span>
          </a>
          <ul class="sub-menu" data-titulo="Financeiro">
            <li v-if="childVisible('financeiro', 'apvsbrasil')">
              <router-link tag="a" to="/notasfiscais/apvsbrasil" class="side-nav-link-ref">
                <span class="notranslate">APVS Brasil</span>
              </router-link>
            </li>
            <li v-if="childVisible('financeiro', 'apvstruck')">
              <router-link tag="a" to="/notasfiscais/apvstruck" class="side-nav-link-ref">
                <span class="notranslate">APVS Truck</span>
              </router-link>
            </li>
          </ul>
        </li>
        <li v-if="menuVisible('suporte')" data-tour="menu-suporte">
          <a class="is-parent has-arrow" href="javascript:void(0);">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path
                d="M12 2C6.486 2 2 6.486 2 12v4.143C2 17.167 2.897 18 4 18h1a1 1 0 0 0 1-1v-5.143a1 1 0 0 0-1-1h-.908C4.648 6.987 7.978 4 12 4s7.352 2.987 7.908 6.857H19a1 1 0 0 0-1 1V18c0 1.103-.897 2-2 2h-2v-1h-4v3h6c2.206 0 4-1.794 4-4 1.103 0 2-.833 2-1.857V12c0-5.514-4.486-10-10-10z">
              </path>
            </svg>
            <span class="notranslate">Suporte</span>
          </a>
          <ul class="sub-menu" data-titulo="Suporte">
            <li v-if="childVisible('suporte', 'faq')">
              <router-link tag="a" to="/faq" class="side-nav-link-ref">
                <span class="notranslate">FAQ</span>
              </router-link>
            </li>
            <li v-if="childVisible('suporte', 'tickets')">
              <router-link tag="a" to="/tickets" class="side-nav-link-ref">
                <span class="notranslate">Tickets</span>
              </router-link>
            </li>
          </ul>
        </li>
        <li v-if="menuVisible('guia')" data-tour="menu-guia">
          <router-link tag="a" to="/guia-do-app" class="side-nav-link-ref">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path
                d="M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M10,16.5L16,12L10,7.5V16.5Z">
              </path>
            </svg>
            <span class="notranslate">Guia do App</span>
          </router-link>
        </li>
      </ul>

      <b-modal
        v-model="communityModalVisible"
        modal-class="modal-fullscreen-app"
        hide-header
        hide-footer
        no-close-on-backdrop
        @hidden="closeCommunityModal"
      >
        <div class="community-modal-bar d-flex align-items-center justify-content-between">
          <span class="notranslate">Comunidade</span>
          <button type="button" class="btn btn-sm btn-light" @click="closeCommunityModal">
            <i class="mdi mdi-close"></i>
          </button>
        </div>
        <iframe
          v-if="communityIframeSrc"
          :src="communityIframeSrc"
          class="community-modal-iframe"
          frameborder="0"
        ></iframe>
      </b-modal>
    </nav>
  </template>

  <style scoped>
  .py-2 {
    padding: 0.9rem 1.5rem 0.7rem !important;
  }
  .py-2 i,
  .py-2 span {
    line-height: 0px !important;
    padding-bottom: 0 !important;
  }
  .py-2 i {
    margin-top: -4px;
  }
  .community-modal-bar {
    flex: 0 0 auto;
    padding: 12px 16px;
    border-bottom: 1px solid var(--st-border-soft);
  }
  .community-modal-iframe {
    flex: 1 1 auto;
    width: 100%;
    height: 100%;
    border: 0;
  }
  </style>
